import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import {
  ArrowLeftIcon,
  ServerIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  KeyIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  Cog6ToothIcon,
  PhoneArrowUpRightIcon,
  PhoneArrowDownLeftIcon,
  ClockIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import logger from '../../../utils/logger';

const TrunkManagement = ({ showNotification }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [trunks, setTrunks] = useState([]);
  const [selectedTrunk, setSelectedTrunk] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCredentialModal, setShowCredentialModal] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [loadingCredentials, setLoadingCredentials] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState({});

  // Form state for trunk creation/editing
  const [trunkForm, setTrunkForm] = useState({
    trunkName: '',
    authMethod: 'digest',
    maxConcurrentCalls: 10,
    maxCallsPerSecond: 5,
    billingType: 'postpaid',
    codecs: {
      audio: ['G711U', 'G711A', 'G729', 'OPUS'],
      t38Enabled: false
    },
    dtmfMode: 'rfc2833',
    encryption: {
      srtp: 'optional',
      tlsEnabled: true
    },
    recordingPolicy: 'none'
  });

  // Form state for credential creation
  const [credentialForm, setCredentialForm] = useState({
    username: '',
    password: '',
    displayName: '',
    callerId: { name: '', number: '' },
    permissions: {
      canMakeOutbound: true,
      canReceiveInbound: true,
      allowInternational: false,
      allowPremium: false
    },
    maxConcurrentCalls: 2,
    dailyCallLimit: 0
  });

  useEffect(() => {
    fetchTrunks();
  }, []);

  const fetchTrunks = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/sip/trunks');
      if (response.data.success) {
        // API returns { trunks: [], total, limit, skip }
        setTrunks(response.data.data?.trunks || []);
      }
    } catch (error) {
      logger.error('Error fetching trunks:', error);
      showNotification?.('Failed to load SIP trunks', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCredentials = async (trunkId) => {
    try {
      setLoadingCredentials(true);
      const response = await axiosInstance.get(`/api/sip/trunks/${trunkId}/credentials`);
      if (response.data.success) {
        // API returns { credentials: [], total, limit, skip }
        setCredentials(response.data.data?.credentials || []);
      }
    } catch (error) {
      logger.error('Error fetching credentials:', error);
      showNotification?.('Failed to load credentials', 'error');
    } finally {
      setLoadingCredentials(false);
    }
  };

  const handleCreateTrunk = async () => {
    try {
      setSaving(true);
      const response = await axiosInstance.post('/api/sip/trunks', trunkForm);
      if (response.data.success) {
        showNotification?.('SIP trunk created successfully', 'success');
        setShowCreateModal(false);
        resetTrunkForm();
        fetchTrunks();
      }
    } catch (error) {
      logger.error('Error creating trunk:', error);
      const errorMessage = error.response?.data?.error || 'Failed to create SIP trunk';
      showNotification?.(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTrunk = async () => {
    try {
      setSaving(true);
      const response = await axiosInstance.put(`/api/sip/trunks/${selectedTrunk._id}`, trunkForm);
      if (response.data.success) {
        showNotification?.('SIP trunk updated successfully', 'success');
        setShowCreateModal(false);
        setSelectedTrunk(null);
        resetTrunkForm();
        fetchTrunks();
      }
    } catch (error) {
      logger.error('Error updating trunk:', error);
      const errorMessage = error.response?.data?.error || 'Failed to update SIP trunk';
      showNotification?.(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTrunk = async (trunkId) => {
    if (!window.confirm('Are you sure you want to delete this SIP trunk? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await axiosInstance.delete(`/api/sip/trunks/${trunkId}`);
      if (response.data.success) {
        showNotification?.('SIP trunk deleted successfully', 'success');
        fetchTrunks();
      }
    } catch (error) {
      logger.error('Error deleting trunk:', error);
      showNotification?.('Failed to delete SIP trunk', 'error');
    }
  };

  const handleProvisionTrunk = async (trunkId) => {
    try {
      const response = await axiosInstance.post(`/api/sip/trunks/${trunkId}/provision`);
      if (response.data.success) {
        showNotification?.('SIP trunk provisioned successfully', 'success');
        fetchTrunks();
      }
    } catch (error) {
      logger.error('Error provisioning trunk:', error);
      showNotification?.('Failed to provision SIP trunk', 'error');
    }
  };

  const handleSuspendTrunk = async (trunkId) => {
    try {
      const response = await axiosInstance.post(`/api/sip/trunks/${trunkId}/suspend`);
      if (response.data.success) {
        showNotification?.('SIP trunk suspended', 'success');
        fetchTrunks();
      }
    } catch (error) {
      logger.error('Error suspending trunk:', error);
      showNotification?.('Failed to suspend SIP trunk', 'error');
    }
  };

  const handleCreateCredential = async () => {
    try {
      setSaving(true);
      const response = await axiosInstance.post(
        `/api/sip/trunks/${selectedTrunk._id}/credentials`,
        credentialForm
      );
      if (response.data.success) {
        showNotification?.('Credential created successfully', 'success');
        setShowCredentialModal(false);
        resetCredentialForm();
        fetchCredentials(selectedTrunk._id);
      }
    } catch (error) {
      logger.error('Error creating credential:', error);
      const errorMessage = error.response?.data?.error || 'Failed to create credential';
      showNotification?.(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCredential = async (credentialId) => {
    if (!window.confirm('Are you sure you want to delete this credential?')) {
      return;
    }

    try {
      const response = await axiosInstance.delete(`/api/sip/credentials/${credentialId}`);
      if (response.data.success) {
        showNotification?.('Credential deleted successfully', 'success');
        fetchCredentials(selectedTrunk._id);
      }
    } catch (error) {
      logger.error('Error deleting credential:', error);
      showNotification?.('Failed to delete credential', 'error');
    }
  };

  const resetTrunkForm = () => {
    setTrunkForm({
      trunkName: '',
      authMethod: 'digest',
      maxConcurrentCalls: 10,
      maxCallsPerSecond: 5,
      billingType: 'postpaid',
      codecs: {
        audio: ['G711U', 'G711A', 'G729', 'OPUS'],
        t38Enabled: false
      },
      dtmfMode: 'rfc2833',
      encryption: {
        srtp: 'optional',
        tlsEnabled: true
      },
      recordingPolicy: 'none'
    });
  };

  const resetCredentialForm = () => {
    setCredentialForm({
      username: '',
      password: '',
      displayName: '',
      callerId: { name: '', number: '' },
      permissions: {
        canMakeOutbound: true,
        canReceiveInbound: true,
        allowInternational: false,
        allowPremium: false
      },
      maxConcurrentCalls: 2,
      dailyCallLimit: 0
    });
  };

  const openEditModal = (trunk) => {
    setSelectedTrunk(trunk);
    setTrunkForm({
      trunkName: trunk.trunkName,
      authMethod: trunk.authMethod,
      maxConcurrentCalls: trunk.maxConcurrentCalls,
      maxCallsPerSecond: trunk.maxCallsPerSecond,
      billingType: trunk.billingType,
      codecs: trunk.codecs || { audio: ['G711U', 'G711A', 'G729', 'OPUS'], t38Enabled: false },
      dtmfMode: trunk.dtmfMode,
      encryption: trunk.encryption || { srtp: 'optional', tlsEnabled: true },
      recordingPolicy: trunk.recordingPolicy
    });
    setShowCreateModal(true);
  };

  const openCredentialsPanel = (trunk) => {
    setSelectedTrunk(trunk);
    fetchCredentials(trunk._id);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircleIcon },
      suspended: { color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: XCircleIcon },
      pending: { color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', icon: ClockIcon },
      provisioning: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Cog6ToothIcon },
      inactive: { color: 'bg-gray-100 text-gray-700 dark:bg-[#1a1a1a] dark:text-gray-400', icon: ExclamationCircleIcon }
    };

    const config = statusConfig[status] || statusConfig.inactive;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    );
  };

  const getRegistrationBadge = (regStatus) => {
    if (regStatus === 'registered') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
          <CheckCircleIcon className="w-3 h-3" />
          Online
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 dark:bg-[#1a1a1a] dark:text-gray-400">
        <XCircleIcon className="w-3 h-3" />
        Offline
      </span>
    );
  };

  return (
    <div className="h-full bg-white dark:bg-[#111111] overflow-y-auto">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/voice')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  SIP Trunks
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Manage your SIP trunk configurations and credentials
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setSelectedTrunk(null);
                resetTrunkForm();
                setShowCreateModal(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-orange-600 text-white hover:bg-orange-700 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              New Trunk
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-6">
          {/* Trunk List */}
          <div className={`${selectedTrunk && !showCreateModal ? 'w-1/2' : 'w-full'} transition-all duration-300`}>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
              </div>
            ) : trunks.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 dark:bg-[#1a1a1a] rounded-lg">
                <ServerIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No SIP trunks</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Get started by creating a new SIP trunk
                </p>
                <button
                  onClick={() => {
                    setSelectedTrunk(null);
                    resetTrunkForm();
                    setShowCreateModal(true);
                  }}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-orange-600 text-white hover:bg-orange-700 transition-colors"
                >
                  <PlusIcon className="w-4 h-4" />
                  Create SIP Trunk
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {trunks.map((trunk) => (
                  <div
                    key={trunk._id}
                    className={`border rounded-lg p-6 transition-all cursor-pointer ${
                      selectedTrunk?._id === trunk._id && !showCreateModal
                        ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/10'
                        : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
                    }`}
                    onClick={() => openCredentialsPanel(trunk)}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                          <ServerIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {trunk.trunkName}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {trunk.trunkDomain}
                          </p>
                        </div>
                      </div>
                      {getStatusBadge(trunk.status)}
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-4 gap-4 mb-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                          {trunk.stats?.totalCallsInbound || 0}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Inbound</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                          {trunk.stats?.totalCallsOutbound || 0}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Outbound</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                          {trunk.maxConcurrentCalls}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Max Calls</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                          {trunk.sipCredentials?.length || 0}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Credentials</p>
                      </div>
                    </div>

                    {/* Info Tags */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-gray-100 dark:bg-[#222222] text-gray-600 dark:text-gray-400">
                        <ShieldCheckIcon className="w-3 h-3" />
                        {trunk.authMethod}
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-gray-100 dark:bg-[#222222] text-gray-600 dark:text-gray-400">
                        <GlobeAltIcon className="w-3 h-3" />
                        {trunk.encryption?.tlsEnabled ? 'TLS' : 'No TLS'}
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-gray-100 dark:bg-[#222222] text-gray-600 dark:text-gray-400">
                        {trunk.billingType}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-800">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(trunk);
                        }}
                        className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#222222] transition-colors"
                      >
                        <PencilIcon className="w-4 h-4" />
                        Edit
                      </button>
                      {trunk.status === 'pending' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleProvisionTrunk(trunk._id);
                          }}
                          className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
                        >
                          <CheckCircleIcon className="w-4 h-4" />
                          Provision
                        </button>
                      )}
                      {trunk.status === 'active' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSuspendTrunk(trunk._id);
                          }}
                          className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium rounded-lg bg-yellow-600 text-white hover:bg-yellow-700 transition-colors"
                        >
                          <ExclamationCircleIcon className="w-4 h-4" />
                          Suspend
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTrunk(trunk._id);
                        }}
                        className="px-3 py-2 text-sm font-medium rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Credentials Panel */}
          {selectedTrunk && !showCreateModal && (
            <div className="w-1/2 border border-gray-200 dark:border-gray-800 rounded-lg">
              <div className="p-4 border-b border-gray-200 dark:border-gray-800">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      Credentials - {selectedTrunk.trunkName}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      SIP users for this trunk
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        resetCredentialForm();
                        setShowCredentialModal(true);
                      }}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg bg-orange-600 text-white hover:bg-orange-700 transition-colors"
                    >
                      <PlusIcon className="w-4 h-4" />
                      Add
                    </button>
                    <button
                      onClick={() => setSelectedTrunk(null)}
                      className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    >
                      <XCircleIcon className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-4 max-h-[600px] overflow-y-auto">
                {loadingCredentials ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 dark:border-gray-100"></div>
                  </div>
                ) : credentials.length === 0 ? (
                  <div className="text-center py-8">
                    <KeyIcon className="mx-auto h-10 w-10 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      No credentials yet
                    </p>
                    <button
                      onClick={() => {
                        resetCredentialForm();
                        setShowCredentialModal(true);
                      }}
                      className="mt-3 text-sm text-orange-600 dark:text-orange-400 hover:underline"
                    >
                      Create first credential
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {credentials.map((cred) => (
                      <div
                        key={cred._id}
                        className="border border-gray-200 dark:border-gray-800 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <KeyIcon className="w-4 h-4 text-gray-500" />
                              <span className="font-medium text-gray-900 dark:text-white">
                                {cred.username}
                              </span>
                            </div>
                            {cred.displayName && (
                              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                {cred.displayName}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {getRegistrationBadge(cred.registrationStatus)}
                          </div>
                        </div>

                        {/* Permissions */}
                        <div className="flex flex-wrap gap-1 mb-3">
                          {cred.permissions?.canMakeOutbound && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                              <PhoneArrowUpRightIcon className="w-3 h-3" />
                              Outbound
                            </span>
                          )}
                          {cred.permissions?.canReceiveInbound && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                              <PhoneArrowDownLeftIcon className="w-3 h-3" />
                              Inbound
                            </span>
                          )}
                          {cred.permissions?.allowInternational && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
                              <GlobeAltIcon className="w-3 h-3" />
                              International
                            </span>
                          )}
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-3 gap-2 text-center mb-3">
                          <div>
                            <p className="text-lg font-semibold text-gray-900 dark:text-white">
                              {cred.stats?.totalCalls || 0}
                            </p>
                            <p className="text-xs text-gray-500">Calls</p>
                          </div>
                          <div>
                            <p className="text-lg font-semibold text-gray-900 dark:text-white">
                              {cred.stats?.totalMinutes || 0}
                            </p>
                            <p className="text-xs text-gray-500">Minutes</p>
                          </div>
                          <div>
                            <p className="text-lg font-semibold text-gray-900 dark:text-white">
                              {cred.maxConcurrentCalls}
                            </p>
                            <p className="text-xs text-gray-500">Max Calls</p>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end">
                          <button
                            onClick={() => handleDeleteCredential(cred._id)}
                            className="px-2 py-1 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Trunk Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
              {selectedTrunk ? 'Edit SIP Trunk' : 'Create SIP Trunk'}
            </h2>

            <div className="space-y-6">
              {/* Basic Info */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Basic Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Trunk Name *
                    </label>
                    <input
                      type="text"
                      value={trunkForm.trunkName}
                      onChange={(e) => setTrunkForm({ ...trunkForm, trunkName: e.target.value })}
                      placeholder="Main Office"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Authentication Method
                    </label>
                    <select
                      value={trunkForm.authMethod}
                      onChange={(e) => setTrunkForm({ ...trunkForm, authMethod: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                    >
                      <option value="digest">Digest (Username/Password)</option>
                      <option value="ip">IP Whitelist</option>
                      <option value="both">Both</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Capacity */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Capacity Settings
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Max Concurrent Calls
                    </label>
                    <input
                      type="number"
                      value={trunkForm.maxConcurrentCalls}
                      onChange={(e) => setTrunkForm({ ...trunkForm, maxConcurrentCalls: parseInt(e.target.value) })}
                      min="1"
                      max="10000"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Max Calls Per Second
                    </label>
                    <input
                      type="number"
                      value={trunkForm.maxCallsPerSecond}
                      onChange={(e) => setTrunkForm({ ...trunkForm, maxCallsPerSecond: parseInt(e.target.value) })}
                      min="1"
                      max="100"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Media Settings */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Media Settings
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      DTMF Mode
                    </label>
                    <select
                      value={trunkForm.dtmfMode}
                      onChange={(e) => setTrunkForm({ ...trunkForm, dtmfMode: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                    >
                      <option value="rfc2833">RFC 2833</option>
                      <option value="inband">In-Band</option>
                      <option value="info">SIP INFO</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Recording Policy
                    </label>
                    <select
                      value={trunkForm.recordingPolicy}
                      onChange={(e) => setTrunkForm({ ...trunkForm, recordingPolicy: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                    >
                      <option value="none">None</option>
                      <option value="inbound">Inbound Only</option>
                      <option value="outbound">Outbound Only</option>
                      <option value="all">All Calls</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Security */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Security
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      SRTP
                    </label>
                    <select
                      value={trunkForm.encryption?.srtp || 'optional'}
                      onChange={(e) => setTrunkForm({
                        ...trunkForm,
                        encryption: { ...trunkForm.encryption, srtp: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                    >
                      <option value="disabled">Disabled</option>
                      <option value="optional">Optional</option>
                      <option value="required">Required</option>
                    </select>
                  </div>
                  <div className="flex items-center pt-6">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={trunkForm.encryption?.tlsEnabled ?? true}
                        onChange={(e) => setTrunkForm({
                          ...trunkForm,
                          encryption: { ...trunkForm.encryption, tlsEnabled: e.target.checked }
                        })}
                        className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Enable TLS
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Billing */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Billing
                </h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Billing Type
                  </label>
                  <select
                    value={trunkForm.billingType}
                    onChange={(e) => setTrunkForm({ ...trunkForm, billingType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                  >
                    <option value="postpaid">Postpaid</option>
                    <option value="prepaid">Prepaid</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setSelectedTrunk(null);
                  resetTrunkForm();
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#222222] rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={selectedTrunk ? handleUpdateTrunk : handleCreateTrunk}
                disabled={saving || !trunkForm.trunkName}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : selectedTrunk ? 'Update Trunk' : 'Create Trunk'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Credential Modal */}
      {showCredentialModal && selectedTrunk && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
              Create SIP Credential
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Username *
                </label>
                <input
                  type="text"
                  value={credentialForm.username}
                  onChange={(e) => setCredentialForm({ ...credentialForm, username: e.target.value })}
                  placeholder="ext101"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Password *
                </label>
                <div className="relative">
                  <input
                    type={showPassword.credential ? 'text' : 'password'}
                    value={credentialForm.password}
                    onChange={(e) => setCredentialForm({ ...credentialForm, password: e.target.value })}
                    placeholder="Strong password"
                    className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword({ ...showPassword, credential: !showPassword.credential })}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
                  >
                    {showPassword.credential ? (
                      <EyeSlashIcon className="w-5 h-5" />
                    ) : (
                      <EyeIcon className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Display Name
                </label>
                <input
                  type="text"
                  value={credentialForm.displayName}
                  onChange={(e) => setCredentialForm({ ...credentialForm, displayName: e.target.value })}
                  placeholder="John Doe"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Max Concurrent Calls
                </label>
                <input
                  type="number"
                  value={credentialForm.maxConcurrentCalls}
                  onChange={(e) => setCredentialForm({ ...credentialForm, maxConcurrentCalls: parseInt(e.target.value) })}
                  min="1"
                  max="100"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-orange-500 dark:bg-[#2a2a2a] dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Permissions
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={credentialForm.permissions.canMakeOutbound}
                      onChange={(e) => setCredentialForm({
                        ...credentialForm,
                        permissions: { ...credentialForm.permissions, canMakeOutbound: e.target.checked }
                      })}
                      className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      Can make outbound calls
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={credentialForm.permissions.canReceiveInbound}
                      onChange={(e) => setCredentialForm({
                        ...credentialForm,
                        permissions: { ...credentialForm.permissions, canReceiveInbound: e.target.checked }
                      })}
                      className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      Can receive inbound calls
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={credentialForm.permissions.allowInternational}
                      onChange={(e) => setCredentialForm({
                        ...credentialForm,
                        permissions: { ...credentialForm.permissions, allowInternational: e.target.checked }
                      })}
                      className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      Allow international calls
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCredentialModal(false);
                  resetCredentialForm();
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#222222] rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCredential}
                disabled={saving || !credentialForm.username || !credentialForm.password}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {saving ? 'Creating...' : 'Create Credential'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrunkManagement;
